/**
 * MeshViewer renders a generated GLB mesh in a react-three-fiber canvas.
 *
 * The src URL is expected to be a downloadable GLB (a Firebase Storage
 * download URL or a local blob URL). `useGLTF` suspends while loading, so
 * the model component must be rendered inside a `<Suspense>` boundary.
 *
 * `<Bounds fit clip observe>` from drei wraps the scene so that the
 * camera auto-frames the model regardless of its arbitrary scale; this
 * matters because Tripo emits meshes at wildly varying real-world sizes
 * and a fixed camera position would either dwarf or clip them.
 */

import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { Bounds, Environment, OrbitControls, useGLTF } from '@react-three/drei';

interface Props {
  src: string;
}

function GltfModel({ src }: { src: string }) {
  const gltf = useGLTF(src);
  return <primitive object={gltf.scene} />;
}

export function MeshViewer({ src }: Props) {
  return (
    <div className="h-full w-full bg-synergy-neutral-900">
      <Canvas camera={{ position: [0, 0, 5], fov: 35 }}>
        <ambientLight intensity={0.4} />
        <Suspense fallback={null}>
          <Bounds fit clip observe margin={1.2}>
            <GltfModel src={src} />
          </Bounds>
          <Environment preset="studio" />
        </Suspense>
        <OrbitControls makeDefault enableDamping />
      </Canvas>
    </div>
  );
}

export default MeshViewer;
